import '@css/app.css';
import '@css/legacy-style.css';
import './bootstrap';
import '@/Scripts/Common/polyfills.js';
import 'swiper/css';
import '@css/vendors/swiper/css/overrides/swiper.css';
import 'swiper/css/navigation';
import '@css/vendors/swiper/css/overrides/navigation.css';
import {PageLoadingProvider} from '@/Components/Common/Providers/PageLoadingProvider.jsx';
import {SnackbarProvider} from '@/Components/Common/Providers/SnackbarProvider.jsx';
import MainLayout from '@/Layouts/MainLayout.jsx';
import {GlobalStyle} from '@/Styles/global.js';
import {theme} from '@/Themes/Mistubishi/theme.js';
import createCache from '@emotion/cache';
import {CacheProvider, Global} from '@emotion/react';
import {createInertiaApp, router} from '@inertiajs/react';
import {CssBaseline} from '@mui/material';
import {ThemeProvider} from '@mui/material/styles';
import axios from 'axios';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import React from 'react';
import {createRoot} from 'react-dom/client';

const appName = import.meta.env.VITE_APP_NAME || 'SelectME v.2';

function setScrollRegion(htmlElement) {
    htmlElement.setAttribute('scroll-region', 'true');
}

const public_routes_names = [
    'welcome',
    'register',
    'login',
    'password.request',
    'password.email',
    'password.reset',
    'password.store',
];

axios.interceptors.request.use(
    (config) => {
        if (config?.options) {
            config = {...config, ...config.options};
            delete config.options; // Remove options after merging
        }
        if (['put', 'delete', 'patch'].includes(config.method)) {
            if (config.data) {
                if (config.data instanceof FormData) {
                    config.data.append('_method', config.method);
                } else {
                    config.data = {...config.data, _method: config.method};
                }
            } else {
                config.data = {_method: config.method};
            }
            config.method = 'post';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: async (name) => {
        const page = await resolvePageComponent(`./Pages/${name}.jsx`, import.meta.glob('./Pages/**/*.jsx'));

        if (
            name.startsWith('PDF/') ||
            name.startsWith('AirAir/PDF/') ||
            name.startsWith('AirAir/Multisplit/PDF/') ||
            name.startsWith('AirWater/PDF/')
        ) {
            return page;
        }
        page.default.layout = (page) => <MainLayout children={page} />;
        return page;
    },
    setup({el, App, props}) {
        const root = createRoot(el);
        let previous_user_state;
        let is_popstate_request = false;

        setScrollRegion(document.getElementById('app'));

        window.addEventListener('popstate', function () {
            is_popstate_request = true;
        });

        router.on('navigate', (e) => {
            const current_route_name = route().current();
            const is_public_route = public_routes_names.includes(current_route_name);
            if (!is_public_route && !previous_user_state && is_popstate_request) {
                location.reload();
            }
            previous_user_state = e.detail.page.props?.auth?.user;
            is_popstate_request = false;
        });

        const nonce = window.__CSP_NONCE__ || '';

        const cache = createCache({
            key: 'my-app',
            nonce: nonce,
            prepend: true,
        });

        root.render(
            <>
                <CacheProvider value={cache}>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <Global styles={GlobalStyle} />
                        <PageLoadingProvider>
                            <SnackbarProvider>
                                <App {...props} />
                            </SnackbarProvider>
                        </PageLoadingProvider>
                    </ThemeProvider>
                </CacheProvider>
            </>,
        );
    },
    progress: {
        color: '#4B5563',
    },
});
